import { Reveal } from "@/lib/motion";
import { BOOK_REVIEW_MAILTO } from "@/lib/brand";
import { Layers, MessageSquareText, Cpu, Sparkles, ArrowUpRight } from "lucide-react";

const PILLARS = [
  {
    n: "01",
    icon: Layers,
    title: "Business Structure",
    intro:
      "Clarify what you sell, how you deliver it, and the operating rhythm your team actually runs on.",
    bullets: [
      "Offer architecture & service productisation",
      "Pricing structure & proposal templates",
      "Operating cadence & meeting structure",
      "Roles, responsibilities & accountability",
    ],
    image:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80&auto=format&fit=crop",
  },
  {
    n: "02",
    icon: MessageSquareText,
    title: "Marketing Communications",
    intro:
      "The voice and visible system that earns trust before, during and after the sale.",
    bullets: [
      "Brand positioning & messaging",
      "Website structure & content",
      "Sales follow-up & nurture systems",
      "Proposal & document templates",
    ],
    image:
      "https://images.unsplash.com/photo-1517842645767-c639042777db?w=1200&q=80&auto=format&fit=crop",
  },
  {
    n: "03",
    icon: Cpu,
    title: "Technology Systems",
    intro:
      "Choose the right tools, connect them sensibly, and remove the hidden double-handling.",
    bullets: [
      "Stack review & rationalisation",
      "Workflow design & process mapping",
      "Integrations, data and dashboards",
      "Documented operating system",
    ],
    image:
      "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=1200&q=80&auto=format&fit=crop",
  },
  {
    n: "04",
    icon: Sparkles,
    title: "Practical AI Adoption",
    intro:
      "Embed AI into the parts of the day that actually matter — admin, capture, summarising, generating.",
    bullets: [
      "Voice capture & on-the-go inputs",
      "Structured prompts & playbooks",
      "Document and report generation",
      "Task movement & follow-through",
    ],
    image:
      "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1200&q=80&auto=format&fit=crop",
  },
];

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-32 md:pt-40">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <div className="overline">Services</div>
        </Reveal>
        <Reveal delay={1}>
          <h1 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tightest max-w-5xl">
            Four pillars.
            <em className="font-light text-ink-soft"> One way of working.</em>
          </h1>
        </Reveal>
        <Reveal delay={2}>
          <p className="mt-10 max-w-2xl text-lg text-ink/75 leading-relaxed">
            Engage us as a strategic partner across structure, marketing,
            technology and AI — or step in to one pillar at a time. Every
            engagement starts with a Business Systems Review.
          </p>
        </Reveal>
      </section>

      {/* Pillars */}
      <section className="mt-24 md:mt-32 space-y-24 md:space-y-32">
        {PILLARS.map((p, i) => {
          const Icon = p.icon;
          const flip = i % 2 === 1;
          return (
            <div
              key={p.n}
              data-testid={`services-pillar-${i + 1}`}
              className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
            >
              <Reveal
                className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}
              >
                <div className="relative aspect-[4/3] bg-bg-muted overflow-hidden max-w-md">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-ink/30 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-3 bg-white/95 px-3 py-1.5">
                    <Icon size={14} strokeWidth={1.6} className="text-brand-red" />
                    <span className="overline text-[10px]">{p.n}</span>
                  </div>
                </div>
              </Reveal>
              <Reveal className="lg:col-span-7" delay={1}>
                <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight leading-tight">
                  {p.title}
                </h2>
                <p className="mt-6 text-lg text-ink/75 leading-relaxed max-w-xl">
                  {p.intro}
                </p>
                <ul className="mt-8 space-y-3 max-w-lg">
                  {p.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-[15px] text-ink/85 border-b border-ink/10 pb-3"
                    >
                      <span className="overline text-brand-red mt-1">→</span>
                      {b}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          );
        })}
      </section>

      {/* Specialist offers list */}
      <section className="mt-28 md:mt-36 bg-bg-muted border-y border-ink/10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline">Specialist offers</div>
            <h2 className="mt-6 font-serif-display text-4xl md:text-6xl tracking-tight leading-[0.95] max-w-3xl">
              Productised engagements.
            </h2>
          </Reveal>
          <div className="mt-14 divide-y divide-ink/10 border-y border-ink/10">
            {[
              ["2Tap — Office in your pocket", "Voice-led admin and workflow for site-based businesses.", "https://2tap.app/"],
              ["Contracts Administration Services", "Behind-the-scenes contracts and project admin.", null],
              ["Sales & Marketing Services", "Positioning, website, content and follow-up systems.", null],
              ["Business Systems Review", "A structured deep look at how your business runs today.", null],
              ["AI Workflow Setup", "Practical AI built into the day-to-day.", null],
            ].map(([t, b, href], i) => (
              <Reveal key={t} delay={i}>
                <div
                  data-testid={`services-offer-${i + 1}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-7 md:py-9 items-baseline"
                >
                  <div className="md:col-span-1 overline text-brand-red">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="md:col-span-5 font-serif-display text-2xl md:text-3xl tracking-tight">
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="link-reveal hover:text-brand-red inline-flex items-center gap-2"
                      >
                        {t}
                        <ArrowUpRight size={18} />
                      </a>
                    ) : (
                      t
                    )}
                  </h3>
                  <p className="md:col-span-6 text-ink/75 leading-relaxed text-[15.5px] max-w-xl">
                    {b}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16">
            <a
              href={BOOK_REVIEW_MAILTO}
              data-testid="services-cta"
              className="inline-flex items-center gap-3 bg-ink hover:bg-brand-red text-bg-base px-7 py-4 text-[13.5px] tracking-[0.06em] font-medium transition-colors"
            >
              Book a Review
              <span aria-hidden>→</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
